import { API_BASE_URL } from '../config/api';

/**
 * R2 ključ u bucketu: npr. tuts/video1.mp4 (ime bucketa nije prefiks).
 * Opciono: REACT_APP_TUTORIALS_R2_PREFIX ako hostuješ isti fajl ispod drugog prefiksa.
 */
const getR2TutorialsPrefix = () =>
  (process.env.REACT_APP_TUTORIALS_R2_PREFIX || 'tuts').replace(/^\/+|\/+$/g, '');

/** Lokalna statika (/public): npr. spintasker/tuts/video1.mp4 */
const getPublicTutorialsPrefix = () =>
  (process.env.REACT_APP_TUTORIALS_PUBLIC_PREFIX || 'spintasker/tuts').replace(/^\/+|\/+$/g, '');

/**
 * Kad zameniš fajl na R2 pod istim imenom (npr. video2.mp4), CDN i pregledač često drže stari keš.
 * U tutorials.json za taj unos dodaj "assetVersion": "2" (povećaj pri svakoj novoj verziji videa).
 */
function appendTutorialAssetVersionQuery(url, item) {
  if (!url || typeof url !== 'string') return url;
  const raw = item?.assetVersion ?? item?.cacheBust ?? item?.videoVersion;
  if (raw === undefined || raw === null || String(raw).trim() === '') return url;
  const enc = encodeURIComponent(String(raw));
  const esc = enc.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  if (new RegExp(`[?&]v=${esc}(?:&|#|$)`).test(url)) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}v=${enc}`;
}

/**
 * primary: uglavnom R2 (ako je REACT_APP_R2_PUBLIC_URL) ili relativna putanja /spintasker/tuts/file
 * fallbackVideoUrl: suprotno — lokal kad je primarni R2, da može fallback na grešku (kopirati mp4 u public)
 */
export const normalizeClientManifest = (data) => {
  if (!Array.isArray(data)) return [];
  const r2Base = (process.env.REACT_APP_R2_PUBLIC_URL || '').replace(/\/$/, '');
  const r2Prefix = getR2TutorialsPrefix();
  const publicPrefix = getPublicTutorialsPrefix();

  const resolveObjectKeyFromFile = (rawFile) => {
    let file = String(rawFile).replace(/^\//, '');
    if (!file.includes('/')) {
      file = `${r2Prefix}/${file}`;
    } else if (
      file.startsWith('spintasker/tuts/') ||
      (file.startsWith('tuts/') && !file.startsWith(`${r2Prefix}/`))
    ) {
      file = file.replace(/^spintasker\/tuts\//, `${r2Prefix}/`).replace(/^tuts\//, `${r2Prefix}/`);
    }
    return file;
  };

  return data
    .map((item, index) => {
      const id = item.id ?? `tutorial-${index + 1}`;
      let videoUrl = item.videoUrl || '';
      const fileNameOnly = item.file ? String(item.file).replace(/^.*\//, '') : '';
      const localRelative = fileNameOnly ? `/${publicPrefix}/${fileNameOnly}` : '';

      let fallbackVideoUrl = '';

      if (videoUrl && /^https?:\/\//i.test(videoUrl)) {
        fallbackVideoUrl = localRelative;
      } else if (videoUrl.startsWith('/') && !videoUrl.startsWith('//')) {
        let pathNoLeading = videoUrl.replace(/^\//, '');
        if (
          r2Base &&
          pathNoLeading.startsWith(`${publicPrefix}/`) &&
          publicPrefix !== r2Prefix
        ) {
          pathNoLeading = `${r2Prefix}/${pathNoLeading.slice(publicPrefix.length + 1)}`;
        }
        const expanded = r2Base ? `${r2Base}/${pathNoLeading}` : videoUrl;
        videoUrl = expanded;
        fallbackVideoUrl = r2Base && expanded.startsWith('http') ? localRelative : '';
      } else if (!videoUrl && item.file) {
        const objectKey = resolveObjectKeyFromFile(item.file);
        const primary = r2Base ? `${r2Base}/${objectKey}` : localRelative;
        videoUrl = primary;
        fallbackVideoUrl = r2Base ? localRelative : '';
      }

      return {
        ...item,
        id,
        videoUrl: appendTutorialAssetVersionQuery(videoUrl, item),
        fallbackVideoUrl: appendTutorialAssetVersionQuery(fallbackVideoUrl, item)
      };
    })
    .filter((item) => Boolean(item.videoUrl));
};

/**
 * Prvo API manifest, pa R2 manifest, pa lokalna statika pod public/.
 */
export async function loadTutorialsManifest() {
  let raw = null;
  const r2Base = (process.env.REACT_APP_R2_PUBLIC_URL || '').replace(/\/$/, '');
  const r2Prefix = getR2TutorialsPrefix();
  const publicPrefix = getPublicTutorialsPrefix();

  try {
    const manifestUrl = `${API_BASE_URL}/api/tutorials/manifest`;
    const response = await fetch(manifestUrl, { cache: 'no-store' });
    if (response.ok) {
      const parsed = await response.json();
      if (Array.isArray(parsed) && parsed.length > 0) raw = parsed;
    }
  } catch {
    // fallback
  }

  if ((!raw || raw.length === 0) && r2Base) {
    try {
      const r2ManifestUrl = `${r2Base}/${r2Prefix}/tutorials.json`;
      const r = await fetch(r2ManifestUrl, { cache: 'no-store' });
      if (r.ok) {
        const parsed = await r.json();
        if (Array.isArray(parsed) && parsed.length > 0) raw = parsed;
      }
    } catch {
      // ignore
    }
    if (!raw || raw.length === 0) {
      try {
        const legacy = await fetch(`${r2Base}/tuts/tutorials.json`, { cache: 'no-store' });
        if (legacy.ok) {
          const parsed = await legacy.json();
          if (Array.isArray(parsed) && parsed.length > 0) raw = parsed;
        }
      } catch {
        // ignore
      }
    }
  }

  if (!raw || raw.length === 0) {
    const localCandidates = [`/${publicPrefix}/tutorials.json`, '/tuts/tutorials.json'];
    for (const url of localCandidates) {
      try {
        const r = await fetch(url, { cache: 'no-store' });
        if (r.ok) {
          const parsed = await r.json();
          if (Array.isArray(parsed) && parsed.length > 0) {
            raw = parsed;
            break;
          }
        }
      } catch {
        // next
      }
    }
  }

  return normalizeClientManifest(raw || []);
}

/** Prvi tutorijal je javan; ostali zahtevaju prijavu / registraciju. */
export const FREE_PUBLIC_TUTORIAL_INDEX = 0;
export const FREE_PUBLIC_TUTORIAL_FILE = 'video1.mp4';

export function isPublicTutorialUnlocked(index, isLoggedIn = false) {
  if (isLoggedIn) return true;
  return Number(index) === FREE_PUBLIC_TUTORIAL_INDEX;
}

export function isPublicTutorialFileUnlocked(filePath, isLoggedIn = false) {
  if (isLoggedIn) return true;
  const base = String(filePath || '')
    .replace(/^.*\//, '')
    .split('?')[0]
    .toLowerCase();
  return base === FREE_PUBLIC_TUTORIAL_FILE;
}
